import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { MkCTA } from "@/components/marketing/MarketingLayout";

const FAQS = [
  { q: "Is Castles Creation really free?", a: "Yes, completely. Membership, inductions, city circles, guides and Castles AI all cost nothing, forever. We're a platform built by former students, not a business hunting for your money." },
  { q: "Are you an agency or consultancy?", a: "No. We don't sell admissions, visas or accommodation packages. Castles is a student success platform. Everything here exists to make your life in the UK better after your offer letter, not to sell you anything before it." },
  { q: "I'm still waiting for my visa. Should I join now?", a: "Yes, that's the perfect time. The earlier you join, the more value you get: induction, city introductions and your preparation roadmap all happen before you fly." },
  { q: "What exactly do I get as a member?", a: "A personal UK journey tracker, free pre-flight induction, introductions to students in your city, Castles AI advisor, member-only deals, job and CV guidance, events, trips and city guides. See the Membership page for the full picture." },
  { q: "How do you make money if it's free?", a: "We partner with student-friendly brands for member deals, but we only ever recommend what's genuinely best for students. Membership itself will always be free." },
  { q: "Will you help me find a part-time job?", a: "We give you the full playbook: a UK-style CV, where to apply, how the 20-hour rule works, and referrals from members already working in your city. Members typically start earning much sooner." },
  { q: "Which cities do you cover?", a: "All major UK student cities: London, Manchester, Birmingham, Leeds, Leicester, Coventry, Nottingham, Sheffield, Glasgow, Edinburgh and more. New city circles open as the community grows." },
  { q: "What happens right after I sign up?", a: "You'll complete a quick 2-minute onboarding (saved as you go), get your journey tracker, receive your induction invite, and get introduced to your city circle. Simple, guided, calm." },
];

function Item({ q, a, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-2xl overflow-hidden">
      <button data-testid={testid} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-7 py-6 text-left transition-colors duration-300 hover:bg-slate-50 focus:ring-2 focus:ring-[#D4AF37] focus:outline-none">
        <span className="text-[#0A1633] font-semibold">{q}</span>
        <ChevronDown className={`w-5 h-5 text-[#D4AF37] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-7 pb-7 text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function FAQsPage() {
  return (
    <main data-testid="faqs-page">
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 sm:pt-28 pb-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
          <p className="text-sm font-semibold tracking-[0.15em] uppercase text-[#D4AF37]">FAQs</p>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1633] leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="faqs-heading">
            Honest answers, no fine print.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            The questions every student asks before joining, answered the way we'd answer a friend.
          </p>
        </motion.div>
      </section>

      <section className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 pb-24 sm:pb-32">
        <div className="space-y-4">
          {FAQS.map((f, i) => <Item key={f.q} q={f.q} a={f.a} testid={`faq-item-${i}`} />)}
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="mt-16 text-center bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] rounded-3xl p-12">
          <h2 className="text-2xl font-bold text-[#0A1633]" style={{ fontFamily: "'Outfit', sans-serif" }}>Still wondering something?</h2>
          <p className="mt-3 text-slate-600">Join free and ask Castles AI anything. It never sleeps.</p>
          <div className="mt-7">
            <MkCTA testid="faqs-cta">
              Become a Free Member <ArrowRight className="inline w-4 h-4 ml-1" />
            </MkCTA>
          </div>
        </motion.div>
      </section>
    </main>
  );
}
